// @noflow
import React from 'react';
import { withTheme } from 'react-fela';
import type { Node as React$Node } from 'react';
import Box from './Box';
import Image from './Image';
import type { TextProps } from './Text';
import type { ColorProps, Theme } from '../themes/types';
import { computeTextStyle } from './Text';
import isReactNative from '../app/isReactNative';
import CloseIcon from 'mdi-react/CloseIcon';

export type ButtonProps = ColorProps &
  TextProps & {
    // For blindness accessibility features. Consider making it mandatory.
    accessibilityLabel?: string,
    boxStyle?: (theme: Theme) => Object,
    children?: any,
    disabled?: boolean,
    onPress?: (e?: SyntheticMouseEvent<EventTarget>) => any,
    testID?: string,
    to?: string | Object,
    theme: Theme,
  };

type ButtonContext = {
  Button: () => React$Node,
  router: any,
};

const Button = (
  {
    as,
    accessibilityLabel,
    boxStyle,
    children,
    disabled,
    onPress,
    outline,
    textStyle,
    to,
    theme,
    ...props
  }: ButtonProps,
  { Button: PlatformButton, router }: ButtonContext,
) => {
  if (to) {
    onPress = () => router.history.push(to);
  }
  const platformProps = isReactNative
    ? {
        accessibilityComponentType: 'button',
        accessibilityLabel,
        accessibilityTraits: ['button'],
        activeOpacity: theme.states.active.opacity,
        onPress,
      }
    : {
        onClick: onPress,
      };

  const colorProps = Object.keys(theme.colors);

  // <Button primary
  // any is needed probably because Array find is not yet fully typed.
  const propColor: any = colorProps.find(color => props[color]);
  if (propColor) {
    props = {
      ...props,
      backgroundColor: propColor,
      bold: true,
      color: 'white',
      paddingHorizontal: 1,
    };
  }

  // <Button primary outline
  if (propColor && outline) {
    delete props.backgroundColor;
    props = {
      ...props,
      bold: false,
      borderColor: props.backgroundColor,
      borderStyle: 'solid',
      borderWidth: 1,
      color: props.backgroundColor,
      paddingHorizontal: 1,
    };
  }

  // Give button some vertical space.
  const { size = 0 } = props;
  if (size >= 0) {
    props = {
      marginVertical: 0.3,
      paddingVertical: 0.2,
      ...props,
    };
  } else {
    props = {
      marginVertical: 0.5,
      ...props,
    };
    if (props.borderWidth) {
      props = {
        // Ensure vertical Rhythm for Button size < 0. The lineHeight is the
        // only possible way how to do it. It doesn't work for multilines
        lineHeight: theme.typography.lineHeight - 2 * props.borderWidth || 0,
        ...props,
      };
    }
  }

  const [allBoxProps] = computeTextStyle(theme, props);
  // To prevent "Unknown prop" warning, we have to remove color props.
  const boxProps = colorProps.reduce((props, prop) => {
    delete props[prop];
    return props;
  }, allBoxProps);
  const { borderRadius = theme.button.borderRadius } = props;

  return (
    <Box
      as={PlatformButton}
      borderRadius={borderRadius}
      disabled={disabled} // Do we need that?
      flexDirection="row"
      justifyContent="center"
      opacity={disabled ? theme.states.disabled.opacity : 1}
      {...platformProps}
      {...boxProps}
      style={boxStyle || props.style}
      testID="close-button"
    >
      <Box as={CloseIcon} size={28} color={theme.colors.veryLightGrey} />
    </Box>
  );
};

export default withTheme(Button, 'theme');
